import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredPermission = null, requiredRole = null, requiredRoles = null, loginPath = "/signin" }) => {
  const { isAuthenticated, currentUser, loading, hasPermission, hasRole } = useAuth();
  const location = useLocation();

  // Determine the correct login path based on current route
  const isSuperAdmin = location.pathname.startsWith('/super-admin') ||
                       window.location.pathname.startsWith('/super-admin') ||
                       requiredRole === 'super_admin';
  const redirectPath = isSuperAdmin ? "/" : loginPath;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-200">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Check if user needs to change password (first login with temp password)
  if (currentUser?.password_reset_required && location.pathname !== '/password-change') {
    return <Navigate to="/password-change" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-red-600 text-2xl"></span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-200">You don't have permission to access this resource. Required permission: {requiredPermission}</p>
        </div>
      </div>
    );
  }

  if (requiredRoles && Array.isArray(requiredRoles)) {
    const allowed = requiredRoles.some((r) => hasRole(r));
    if (!allowed) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <span className="text-red-600 text-2xl"></span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Insufficient Role</h2>
            <p className="text-gray-600 dark:text-gray-200">Your role ({currentUser?.role}) doesn't have access. Required roles: {requiredRoles.join(', ')}</p>
          </div>
        </div>
      );
    }
  } else if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-red-600 text-2xl"></span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Insufficient Role</h2>
          <p className="text-gray-600 dark:text-gray-200">Your role ({currentUser?.role}) doesn't have access. Required role: {requiredRole}</p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
